<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />
      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <button
          type="button"
          class="mb-3 inline-flex w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
          @click="goToArrival()"
        >
          <i class="pi pi-arrow-left" />
          Назад
        </button>

        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Поступление товара</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Создание приходной накладной по товарам, количеству и закупочной цене</p>
          </div>
          <Button @click="createArrival()" icon="pi pi-save" label="Сохранить" severity="success" class="!px-3 !py-2 !text-sm !shadow-sm" />
        </div>

        <div class="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
          <div class="mb-5 grid shrink-0 grid-cols-1 gap-3 xl:grid-cols-5">
            <div class="xl:col-span-1">
              <FloatLabel variant="on">
                <label for="on_label">Дата</label>
                <InputText id="on_label" v-model="currentTime" disabled variant="outlined" class="w-full" />
              </FloatLabel>
            </div>
            <div class="xl:col-span-1">
              <FloatLabel variant="on">
                <label for="on_label" class="z-10">Контрагент</label>
                <Select v-model="counterpartyId" showClear :options="counterparties" filter optionLabel="name" optionValue="id" class="w-full" required></Select>
              </FloatLabel>
            </div>
            <div class="xl:col-span-1">
              <FloatLabel variant="on">
                <label for="on_label" class="z-10">Счет</label>
                <Select v-model="account" showClear :options="accounts" filter optionLabel="account" class="w-full" required></Select>
              </FloatLabel>
            </div>
            <div v-if="account" class="xl:col-span-1">
              <FloatLabel variant="on">
                <label for="on_label" aria-disabled>Банк</label>
                <InputText id="on_label" v-model="account.bank" disabled variant="outlined" class="w-full" />
              </FloatLabel>
            </div>
            <div v-if="account" class="xl:col-span-1">
              <FloatLabel variant="on">
                <label for="on_label" class="z-10">Сумма</label>
                <InputNumber id="on_label" v-model="account.amount" disabled variant="outlined" :suffix="' ' + account.currency" class="w-full" />
              </FloatLabel>
            </div>
          </div>

          <Toast />

          <DataTable ref="arrivalTable" :filters="filters" filterDisplay="menu" :globalFilterFields="['bank', 'account', 'currency', 'amount']" :value="arrivals" resizableColumns columnResizeMode="fit" removableSort showGridlines stripedRows size="small" scrollable scrollHeight="flex" class="arrival-form-table h-full">
            <template #header>
              <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div class="flex flex-wrap items-center gap-2">
                  <IconField class="w-full sm:w-auto">
                    <InputIcon>
                      <i class="pi pi-search" />
                    </InputIcon>
                    <InputText v-model="filters['global'].value" placeholder="Поиск по строкам поступления..." />
                  </IconField>
                  <Button @click="clearFilter()" icon="pi pi-eraser" label="Очистить" severity="secondary" variant="outlined" />
                </div>
                <Button @click="addRow()" icon="pi pi-plus" label="Добавить товар" severity="success" variant="outlined" />
              </div>
            </template>
            <template #empty> Не найдено. </template>
            <Column field="barcode" sortable header="Штрихкод" style="width: 20%">
              <template #body="{ data, index }">
                <InputNumber v-model="data.barcode" placeholder="Штрихкод" @value-change=onChangeBarcode($event,index) fluid required :invalid="arrivalSubmitted && !data.barcode"/>
              </template>
            </Column>
            <Column field="name" sortable header="Наимен." style="width: 20%">
              <template #body="{ data, index }">
                <Button @click="newProductDialog = true" icon="pi pi-plus" label="Добавить продукт" severity="danger" variant="outlined" text fluid v-if="data.barcode && !data.name"/>
                <div class="" v-else=""> {{ data.name }}</div>
              </template>
            </Column>
            <Column field="amount" sortable header="Ед. изм." style="width: 5% !important">
              <template #body="{ data, index }" class="w-full">
                <Select v-model="data.unit_id" showClear :options="units" optionLabel="name" optionValue="id" class="w-full md:w-56" required :invalid="arrivalSubmitted && !data.unit_id" fluid width="100"/>
              </template>
            </Column>
            <Column field="expiry_date" sortable header="Срок годности" style="width: 12%">
              <template #body="{ data }">
                <InputText v-model="data.expiry_date" type="date" fluid />
              </template>
            </Column>
            <Column field="amount" sortable header="Количество" style="width: 10%">
              <template #body="{ data, index }">
                <InputNumber v-model="data.amount" placeholder="Количество" fluid required :invalid="arrivalSubmitted && !data.amount"/>
              </template>
            </Column>
            <Column field="price" sortable header="Закуп. цена/ед." style="width: 10%">
              <template #body="{ data, index }">
                <InputNumber v-model="data.price" placeholder="Закуп. цена/ед." fluid required :invalid="arrivalSubmitted && !data.price"/>
              </template>
            </Column>
            <Column field="name" sortable header="Себес. итого" style="width: 10%">
              <template #body="{ data, index }">
                 {{ data.amount * data.price }}
              </template>
            </Column>
            <Column class="w-120 !text-center" style="width: 5%">
              <template #body="{ data, index }">
                <Button icon="pi pi-trash" variant="outlined" @click="removeRow(index)" severity="danger" raised v-tooltip.top="{ value: 'Удалить'}"></Button>
              </template>
            </Column>
          
            <ColumnGroup type="footer" v-if="account">
              <Row>
                  <Column footer="Итого:" :colspan="6" footerStyle="text-align:right" />
                  <Column :footer="total.toLocaleString() + ' ₸'" :invalid="account.amount < total" footerStyle="text-align:right" />
                  <Column />
              </Row>
            </ColumnGroup>
          </DataTable>
        </div>

        <Dialog
          v-model:visible="newProductDialog"
          :style="{ width: '680px', maxWidth: '96vw' }"
          header="Добавить товар"
          :modal="true"
          class="system-form-dialog"
        >
          <div class="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
            <section class="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-slate-900">Основное</h3>
                <p class="mt-1 text-xs text-slate-500">Название, штрихкод и описание товара.</p>
              </div>

              <div class="grid gap-3 md:grid-cols-2">
                <div class="md:col-span-2">
                  <label for="new-name" class="mb-1 block text-sm font-medium text-slate-700">Название</label>
                  <InputText id="new-name" v-model.trim="newProduct.name" :invalid="newProductSubmitted && !newProduct.name" fluid required />
                </div>
                <div class="md:col-span-2">
                  <div class="mb-1 flex items-center justify-between gap-2">
                    <label for="new-barcode" class="block text-sm font-medium text-slate-700">Штрихкод</label>
                    <Button
                      type="button"
                      icon="pi pi-barcode"
                      label="Сгенерировать"
                      severity="secondary"
                      size="small"
                      :loading="barcodeGenerating"
                      @click="generateNewProductBarcode"
                    />
                  </div>
                  <InputText
                    id="new-barcode"
                    v-model.trim="productBarcode"
                    fluid
                    inputmode="numeric"
                    pattern="[0-9]*"
                    placeholder="Введите один штрихкод"
                  />
                </div>
                <div class="md:col-span-2">
                  <label for="new-description" class="mb-1 block text-sm font-medium text-slate-700">Описание</label>
                  <InputText id="new-description" v-model.trim="newProduct.description" fluid />
                </div>
              </div>
            </section>

            <section class="rounded-2xl border border-slate-200 bg-white p-3.5">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-slate-900">Классификация</h3>
                <p class="mt-1 text-xs text-slate-500">Выберите склад, класс и категорию товара.</p>
              </div>

              <div class="grid gap-3 md:grid-cols-2">
                <div>
                  <label for="new-classification" class="mb-1 block text-sm font-medium text-slate-700">Класс</label>
                  <Select id="new-classification" v-model="newProduct.classification_id" @change="onChangeClassification($event)" :options="classifications" :invalid="newProductSubmitted && !newProduct.classification_id" optionLabel="name" optionValue="id" fluid required />
                </div>
                <div>
                  <label for="new-category" class="mb-1 block text-sm font-medium text-slate-700">Категория</label>
                  <Select id="new-category" v-model="newProduct.category_id" :options="categories" :disabled="!newProduct.classification_id" :invalid="newProductSubmitted && !newProduct.category_id" optionLabel="name" optionValue="id" fluid required />
                </div>
                <div class="md:col-span-2">
                  <label for="new-storage" class="mb-1 block text-sm font-medium text-slate-700">Склад</label>
                  <Select id="new-storage" v-model="newProduct.storage_id" :options="storages" :invalid="newProductSubmitted && !newProduct.storage_id" optionLabel="name" optionValue="id" fluid required />
                </div>
              </div>
            </section>

            <section class="rounded-2xl border border-slate-200 bg-white p-3.5">
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-slate-900">Фото</h3>
                <p class="mt-1 text-xs text-slate-500">Добавьте изображение товара при необходимости.</p>
              </div>

              <div class="flex flex-col items-center gap-2.5">
                <FileUpload
                  class="w-full border-2 border-dashed border-slate-200 bg-slate-50"
                  severity="info"
                  mode="basic"
                  accept="image/*"
                  auto
                  fluid
                  customUpload
                  chooseLabel="Добавить фото"
                  :chooseButtonProps="{ severity: 'secondary' }"
                  @select="onSelect"
                />
                <div v-if="preview" class="relative mt-1 inline-block">
                  <img :src="preview" width="136" class="rounded-2xl border border-slate-200 bg-slate-50 object-cover" />
                  <Button
                    icon="pi pi-times"
                    severity="danger"
                    rounded
                    size="small"
                    class="!absolute !right-2 !top-2 !h-8 !w-8 !min-w-8"
                    @click="clearNewProductImage()"
                  />
                </div>
              </div>
            </section>
          </div>
          <template #footer>
            <div class="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button label="Отменить" icon="pi pi-times" @click="newProductDialog = false, newProductSubmitted = false" severity="secondary" outlined class="sm:min-w-32" />
              <Button label="Добавить" icon="pi pi-check" @click="createProduct()" severity="success" class="sm:min-w-32" />
            </div>
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../lib/axios'
import dayjs from 'dayjs'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()
const router = useRouter()
const filters = ref([])
const currentTime = ref(dayjs().format('YYYY-MM-DD'))

let counterpartyId = null
const account = ref()

const barcodes = ref([])
const counterparties = ref([])
const accounts = ref([])
const currencies = ref([])
const products = ref([])
const units = ref([])
const classifications = ref([])
const categories = ref([])
const storages = ref({})

const arrivals = ref([])
const arrivalSubmitted = ref(false)

const productBarcode = ref('')
const newProduct = ref({})
const newProductDialog = ref(false)
const newProductSubmitted = ref(false)
const barcodeGenerating = ref(false)

const selectedFile = ref(null);
const preview = ref(null);
// filters
const initFilters = () => {
    filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
    }
}
initFilters()

const clearFilter = () => {
    initFilters()
}

const normalizeSingleBarcode = (value) => String(value ?? '').replace(/\D+/g, '').trim()

const onSelect = (e) => {
  selectedFile.value = e.files[0];
  preview.value = URL.createObjectURL(selectedFile.value);
};

const clearNewProductImage = () => {
  selectedFile.value = null
  preview.value = null
}

// adding new product to arrival
const addRow = () => {
  if (!account.value) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Выберите счет!', life: 5000})
  } else {
    arrivals.value.push({ counterparty_id: '', product_id: '', barcode: '', date: '', expiry_date: null, user_id: 2, account_id: '', account: '',  bank: '',  currency: '',  unit_id: '', unit: '', amount: '', price: '', net: '' })
  }
}

const generateNewProductBarcode = async () => {
  barcodeGenerating.value = true

  try {
    const { data } = await axios.post('/api/products/barcode')
    const nextBarcode = String(data?.barcode || '').trim()

    if (!nextBarcode) {
      throw new Error('Не удалось сгенерировать штрихкод')
    }

    productBarcode.value = nextBarcode
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Не удалось сгенерировать штрихкод'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
  } finally {
    barcodeGenerating.value = false
  }
}

const removeRow = (index) => {
  arrivals.value.splice(index, 1)
}

const clear = () => {
  counterpartyId = null
  account.value = []
  arrivals.value = []
}

// creating arrival
const createArrival = async () => {
  arrivalSubmitted.value = true
  if (!counterpartyId) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Выберите контрагента!', life: 5000})
  } else if (!account.value) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Выберите счет!', life: 5000})
  } else if (arrivals.value.length == 0) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Добавьте товары!', life: 5000})
  } else {
    const allValid = arrivals.value.every(item =>
      item.barcode &&
      item.name &&
      item.unit_id &&
      item.amount &&
      item.price
    )
    if (!allValid) {
      toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Заполните все красные поля!',   life: 5000})
    } else {
      if (total.value > account.value.amount) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Недостаточно средств для покупки!',   life: 5000})
      } else {
        arrivals.value.forEach(item => {
          item.account_id = account.value.id
          item.counterparty_id = counterpartyId
          item.date = dayjs().format('YYYY-MM-DD HH:mm:ss')
          item.expiry_date = item.expiry_date ? item.expiry_date : null
        })

        const res = await axios.post('/api/arrival', arrivals.value)
        clear()
      }
    }
  }
}

const total = computed(() =>
  arrivals.value.reduce((sum, acc) => sum + (Number(acc.amount) * Number(acc.price) || 0), 0)
)

// on change methods
const onChangeBarcode = (value, index) => {
  if ( value ) {
    let productId = barcodes.value[value] ? barcodes.value[value] : null;
  
    if ( productId ) {
      getProduct(productId).then(product => {
        arrivals.value[index].product_id = product.id
        arrivals.value[index].name = product.name
        arrivals.value[index].classification = product.classification
        arrivals.value[index].category = product.category
        arrivals.value[index].description = product.description
      })
    } else {
        arrivals.value[index].product_id = ''
        arrivals.value[index].name       = ''
        arrivals.value[index].classification = ''
        arrivals.value[index].category       = ''
        arrivals.value[index].description    = ''
    }
  }
  productBarcode.value = value ? String(value) : ''
}

// adding new product 
const createProduct = async () => {
  newProductSubmitted.value = true

  const hasRequiredFields =
    newProduct.value.name &&
    newProduct.value.storage_id &&
    newProduct.value.classification_id &&
    newProduct.value.category_id &&
    normalizeSingleBarcode(productBarcode.value).length > 0

  if (!hasRequiredFields) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Поля не заполнены', life: 5000 })
    return
  }

  const formData = new FormData()
  formData.append('name', newProduct.value.name)
  formData.append('storage_id', String(newProduct.value.storage_id))
  formData.append('classification_id', String(newProduct.value.classification_id))
  formData.append('category_id', String(newProduct.value.category_id))
  formData.append('description', newProduct.value.description || '')

  formData.append('barcode[0]', normalizeSingleBarcode(productBarcode.value))

  if (selectedFile.value instanceof File) {
    formData.append('image', selectedFile.value)
  }

  try {
    await axios.post('/api/products', formData)

    toast.add({ severity:'success', summary: 'Успешно!', detail: 'Товар добавлен', life: 5000 })
    await getProducts()
    await getBarcodes()

    newProduct.value = {}
    productBarcode.value = ''
    clearNewProductImage()
    newProductSubmitted.value = false
    newProductDialog.value = false
  } catch (e) {
    const errorMessage =
      e?.response?.data?.errors?.image?.[0] ||
      e?.response?.data?.message ||
      'Не удалось сохранить товар'
    toast.add({ severity:'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
    console.error(e)
  }
}

const onChangeClassification = async (event) =>{
  newProduct.value.category_id = null
  const res = await axios.get('/api/category-by-class?classification=' + event.value )
  categories.value = res.data
}

// get data
const getProduct = async (productId) => {
  const res = await axios.get('/api/get-product?product_id=' + productId)
  return res.data;
}
const getAccounts = async () => {
  const res = await axios.get('/api/account')
  accounts.value = res.data
}
const getCurrencies = async () => {
  const res = await axios.get('/api/currencies')
  currencies.value = res.data
}
const getCounterparties = async () => {
  const res = await axios.get('/api/counterparties')
  counterparties.value = res.data
}
const getProducts = async () => {
  const res = await axios.get('/api/products')
  products.value = res.data
}
const getUnits = async () => {
  const res = await axios.get('/api/units')
  units.value = res.data
}
const getClassifications = async () => {
  const res = await axios.get('/api/classifications')
  classifications.value = res.data
}
const getStorages = async () => {
    const res = await axios.get('/api/get-storage') 
    storages.value = res.data;
}
const getBarcodes = async () => {
    const res = await axios.get('/api/get-barcodes') 
    barcodes.value = res.data;
}
const goToArrival = () => {
  router.push({ name: 'Arrival' })
}

onMounted(() => {
  getCounterparties()
  getProducts()
  getBarcodes()
  getAccounts()
  getCurrencies()
  getUnits()
  getClassifications()
  getStorages()
})
</script>

<style scoped>
:deep(.system-form-dialog .p-dialog-header) {
  padding: 1rem 1.25rem 0.7rem;
  border-bottom: 1px solid rgb(226 232 240);
}

:deep(.system-form-dialog .p-dialog-content) {
  padding: 1rem 1.25rem;
  background: rgb(255 255 255);
}

:deep(.system-form-dialog .p-dialog-footer) {
  padding: 0.85rem 1.25rem 1rem;
  border-top: 1px solid rgb(226 232 240);
}

:deep(.arrival-form-table .p-datatable-table-container) {
  border-radius: 1rem;
  border: 1px solid rgb(226 232 240);
}

:deep(.arrival-form-table .p-datatable-thead > tr > th) {
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.85rem 1rem;
}

:deep(.arrival-form-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(30 41 59);
  padding: 0.85rem 1rem;
  vertical-align: middle;
}

:deep(.arrival-form-table .p-datatable-tbody > tr:hover) {
  background: rgba(240, 253, 250, 0.7);
}

:deep(.arrival-form-table .p-datatable-tfoot > tr > td) {
  background: rgb(248 250 252);
  color: rgb(15 23 42);
  font-weight: 600;
  border-color: rgb(226 232 240);
}
</style>
